"use client";

import React from "react";
import { Check, AlertCircle, Sparkles, BookOpen, CheckCircle2, ShieldAlert } from "lucide-react";
import { ProcedureSectionData } from "@/lib/procedure-sections/types";
import { SafeHtml } from "@/components/shared/SafeHtml";

function ColoredTitle({ text }: { text?: string }) {
  if (!text) return null;
  const words = text.split(" ");
  if (words.length <= 1) return <span>{text}</span>;
  
  let orangeCount = 1;
  if (words.length >= 6) orangeCount = 3;
  else if (words.length >= 4) orangeCount = 2;

  const firstPart = words.slice(0, words.length - orangeCount).join(" ");
  const lastPart = words.slice(words.length - orangeCount).join(" ");

  return (
    <>
      <span className="text-brand-black">{firstPart} </span>
      <span className="text-brand-orange">{lastPart}</span>
    </>
  );
}

export function RichTextSection({ section, index }: { section: ProcedureSectionData; index: number }) {
  const variant = section.layoutVariant || "rich_standard";

  // 1. CANDIDATE SUITABILITY MATRIX (IDEAL VS NOT RECOMMENDED)
  if (variant === "rich_pros_cons_table") {
    return (
      <div className="w-full max-w-6xl mx-auto">
        <div className="mb-6 md:mb-10 text-center max-w-3xl mx-auto">
          {section.badge && (
            <span className="font-accent text-xs uppercase tracking-wider text-brand-orange-deep font-semibold">
              {section.badge}
            </span>
          )}
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black leading-tight">
            <ColoredTitle text={section.title} />
          </h2>
          {section.subtitle && (
            <p className="mt-3 text-lg text-brand-gray leading-relaxed">{section.subtitle}</p>
          )}
          <div className="mt-4 h-1 w-20 rounded-full bg-brand-orange mx-auto" />
        </div>

        {section.contentHtml && (
          <div className="mb-8 prose prose-lg max-w-none text-brand-gray text-base sm:text-lg cms-content text-justify">
            <SafeHtml html={section.contentHtml} />
          </div>
        )}

        {section.items && section.items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Ideal Candidate Box */}
            <div className="rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-50/60 via-white to-emerald-50/20 p-8 sm:p-10 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex shrink-0 h-10 w-10 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-md">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">Recommended</span>
                  <h3 className="text-xl font-bold text-emerald-950">Ideal Candidates</h3>
                </div>
              </div>
              <div className="space-y-4">
                {section.items.slice(0, Math.ceil(section.items.length / 2)).map((item, itemIdx) => (
                  <div key={itemIdx} className="flex items-start gap-3 text-sm sm:text-base text-brand-gray">
                    <Check className="h-5 w-5 shrink-0 text-emerald-600 mt-0.5" />
                    <div className="text-justify">
                      <strong className="text-brand-black">{item.title}: </strong>
                      <span dangerouslySetInnerHTML={{ __html: item.description }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* When to Consider Alternatives Box */}
            <div className="rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-50/60 via-white to-amber-50/20 p-8 sm:p-10 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex shrink-0 h-10 w-10 items-center justify-center rounded-xl bg-amber-500 text-white shadow-md">
                  <ShieldAlert className="h-6 w-6" />
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-700">Precautionary</span>
                  <h3 className="text-xl font-bold text-amber-950">Special Considerations</h3>
                </div>
              </div>
              <div className="space-y-4">
                {section.items.slice(Math.ceil(section.items.length / 2)).map((item, itemIdx) => (
                  <div key={itemIdx} className="flex items-start gap-3 text-sm sm:text-base text-brand-gray">
                    <AlertCircle className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" />
                    <div className="text-justify">
                      <strong className="text-brand-black">{item.title}: </strong>
                      <span dangerouslySetInnerHTML={{ __html: item.description }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // 2. CLINICAL SUMMARY & KEY TAKEAWAYS BOX
  if (variant === "rich_key_takeaways_box") {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <div className="rounded-3xl border border-brand-orange/30 bg-gradient-to-br from-[#FFF9F5] via-white to-brand-peach/15 p-8 sm:p-14 shadow-xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex shrink-0 h-12 w-12 items-center justify-center rounded-2xl bg-brand-orange text-white shadow-lg shadow-brand-orange/30">
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              {section.badge && (
                <span className="font-accent text-xs uppercase tracking-wider text-brand-orange-deep font-semibold">
                  {section.badge}
                </span>
              )}
              <h2 className="text-2xl sm:text-3xl font-bold text-brand-black">
                <ColoredTitle text={section.title} />
              </h2>
            </div>
          </div>

          {section.subtitle && (
            <p className="text-base sm:text-lg text-brand-gray mb-6 leading-relaxed text-justify">{section.subtitle}</p>
          )}

          {section.contentHtml && (
            <div className="prose prose-lg max-w-none text-brand-gray font-normal leading-relaxed text-base sm:text-lg cms-content text-justify">
              <SafeHtml html={section.contentHtml} />
            </div>
          )}

          {section.items && section.items.length > 0 && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-brand-orange/15">
              {section.items.map((item, itemIdx) => (
                <div key={itemIdx} className="flex items-start gap-3 rounded-2xl bg-white/80 border border-brand-orange/20 p-4 shadow-xs">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-orange mt-0.5" />
                  <div className="text-justify">
                    <strong className="text-brand-black">{item.title}: </strong>
                    <span className="text-sm text-brand-gray" dangerouslySetInnerHTML={{ __html: item.description }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // 3. TWO-COLUMN MAGAZINE EDITORIAL LAYOUT
  if (variant === "rich_two_column_editorial") {
    return (
      <div className="w-full max-w-7xl mx-auto relative px-4 sm:px-8">
        {/* Decorative background blur (Open canvas, no boxes) */}
        <div className="absolute top-40 -left-40 w-[600px] h-[600px] bg-brand-peach/20 rounded-full blur-[120px] pointer-events-none -z-10" />

        <div className="flex flex-col lg:flex-row gap-8 md:gap-16 lg:gap-32">
          {/* Left Column: Sticky Header */}
          <div className="w-full lg:w-1/3">
            <div className="sticky top-32">
              {section.badge && (
                <span className="font-accent text-sm uppercase tracking-[0.2em] text-brand-orange-deep font-bold flex items-center gap-4">
                  <span className="w-8 h-px bg-brand-orange-deep" />
                  {section.badge}
                </span>
              )}
              <h2 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-black text-brand-black leading-[1.1] tracking-tight">
                <ColoredTitle text={section.title} />
              </h2>
              {section.subtitle && (
                <p className="mt-6 text-xl text-brand-gray/80 leading-relaxed font-light text-justify">{section.subtitle}</p>
              )}
            </div>
          </div>

          {/* Right Column: Open Typography Content */}
          <div className="w-full lg:w-2/3">
            {section.contentHtml && (
              <div className="
                prose prose-lg sm:prose-xl max-w-none 
                prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-brand-black 
                prose-h3:text-3xl prose-h3:sm:text-4xl prose-h3:mt-12 md:prose-h3:mt-16 prose-h3:mb-6 md:prose-h3:mb-8 [&>h3:first-child]:mt-0
                prose-p:text-lg prose-p:sm:text-xl prose-p:leading-[1.9] prose-p:text-brand-gray/90 prose-p:mb-8 md:prose-p:mb-12 prose-p:font-light
                prose-strong:font-semibold prose-strong:text-brand-orange-deep
                [&>p:first-of-type]:first-letter:text-7xl [&>p:first-of-type]:first-letter:font-black [&>p:first-of-type]:first-letter:text-brand-orange [&>p:first-of-type]:first-letter:float-left [&>p:first-of-type]:first-letter:mr-4 [&>p:first-of-type]:first-letter:leading-[0.8] [&>p:first-of-type]:first-letter:mt-2
                cms-content text-justify
              ">
                <SafeHtml html={section.contentHtml} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 4. FRAMED LUXURY PAPER CARD
  if (variant === "rich_framed_card") {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <div className="rounded-3xl border-2 border-brand-gray/20 bg-white p-8 sm:p-14 shadow-xl">
          <div className="mb-8">
            {section.badge && (
              <span className="font-accent text-xs uppercase tracking-wider text-brand-orange-deep font-semibold">
                {section.badge}
              </span>
            )}
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-brand-black">
              <ColoredTitle text={section.title} />
            </h2>
            {section.subtitle && (
              <p className="mt-3 text-lg text-brand-gray text-justify">{section.subtitle}</p>
            )}
            <div className="mt-4 h-1 w-20 rounded-full bg-brand-orange" />
          </div>

          {section.contentHtml && (
            <div className="prose prose-lg max-w-none text-brand-gray font-normal leading-relaxed text-base sm:text-lg cms-content text-justify">
              <SafeHtml html={section.contentHtml} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // 5. CLEAN PROSE DOCUMENT (DEFAULT)
  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="mb-8">
        {section.badge && (
          <span className="font-accent text-xs uppercase tracking-wider text-brand-orange-deep font-semibold">
            {section.badge}
          </span>
        )}
        <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black leading-tight">
          <ColoredTitle text={section.title} />
        </h2>
        {section.subtitle && (
          <p className="mt-3 text-lg text-brand-gray text-justify">{section.subtitle}</p>
        )}
        <div className="mt-4 h-1 w-20 rounded-full bg-brand-orange" />
      </div>

      {section.contentHtml && (
        <div className="prose prose-lg max-w-none text-brand-gray font-normal leading-relaxed text-base sm:text-lg cms-content text-justify">
          <SafeHtml html={section.contentHtml} />
        </div>
      )}
    </div>
  );
}
